import { CONTENT_FITNESS_DIMENSIONS } from "contracts/report/content-fitness/dimensions";
import { For, type JSX } from "solid-js";
import { SCAN_SECTION_KEY } from "../../enums/scan";
import { createWebsiteStore } from "../website/store";
import { ScanRouteHeader } from "./ScanRouteHeader";
import { ScanShell } from "./ScanShell";

export function ScanHelp(props: { websiteId: string }) {
  const context = createWebsiteStore(props.websiteId);
  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.HELP}>
      <div class="signal-route-surface signal-help-page">
        <ScanRouteHeader
          websiteId={props.websiteId}
          section="Help"
          title="Understanding Faro Signal"
          subtitle="A concise guide to Content Fitness, evidence, issues and supporting diagnostics."
        />

        <section class="signal-panel signal-help-intro">
          <span class="signal-mini-kicker">The product hierarchy</span>
          <h2>Content Fitness is the headline measure</h2>
          <p>
            Faro assesses pages against assessment criteria, rolls those
            assessments into five dimensions, and then calculates the website
            Content Fitness score. Findings and issues explain what is driving
            the result and what to address next.
          </p>
          <ol class="signal-help-flow" aria-label="Content Fitness hierarchy">
            <li>Website score</li>
            <li>Dimensions</li>
            <li>Subcriteria</li>
            <li>Page assessments</li>
            <li>Findings</li>
            <li>Issues</li>
          </ol>
        </section>

        <section class="signal-panel">
          <div class="signal-panel-head">
            <div>
              <span class="signal-mini-kicker">Five dimensions</span>
              <h2>How the website score is weighted</h2>
              <p>
                A website score averages page results within each scored
                dimension, then applies the canonical weights below.
              </p>
            </div>
          </div>
          <div class="signal-help-dimensions">
            <For each={CONTENT_FITNESS_DIMENSIONS}>
              {(dimension) => (
                <article>
                  <strong>{dimension.label}</strong>
                  <span>{dimension.weight}%</span>
                  <p>{dimension.description}</p>
                </article>
              )}
            </For>
          </div>
        </section>

        <div class="signal-help-grid">
          <HelpCard title="Website score and page score">
            A page score uses that page's available subcriterion assessments.
            The website score aggregates scored pages by dimension before
            weighting the five dimensions. They describe different levels of the
            same model and need not be identical.
          </HelpCard>
          <HelpCard title="Coverage and unavailable evidence">
            Assessment criteria stay visible even when they were not assessed.
            Missing, not-applicable and unable-to-verify results are not scored
            as zero. Partial coverage means the score reflects only the evidence
            that could be assessed.
          </HelpCard>
          <HelpCard title="Finding and issue">
            A finding is an individual page-level observation. An issue
            consolidates related findings into one trackable action that can
            persist and be reverified across scans.
          </HelpCard>
          <HelpCard title="Issue status">
            <strong>Newly detected</strong> means the issue first appeared in
            the latest completed scan. <strong>Still open</strong> means it was
            reproduced and had not previously been verified absent.{" "}
            <strong>Returned after previous fix</strong> means it reappeared
            after an earlier scan verified it absent. <strong>Done</strong> and{" "}
            <strong>Ignored</strong> are manual decisions.{" "}
            <strong>Verified fixed</strong> is set when every affected page was
            completely reassessed and no longer reproduced the issue. The
            lifecycle label <strong>Verified fixed after change</strong>{" "}
            additionally confirms comparable page content changed.{" "}
            <strong>Not reverified</strong> means the latest evidence did not
            satisfy those verification conditions.
          </HelpCard>
          <HelpCard title="Brand Guidelines">
            Brand Guidelines are assessment context, not another score. Active
            rules can inform relevant subcriteria across any of the five
            dimensions; a rule is used only where it applies and where the scan
            can trace it to evidence.
          </HelpCard>
          <HelpCard title="AI Agent Readiness and AI Reference">
            AI Agent Readiness is a weighted Content Fitness dimension about
            whether page content can be accessed, understood and acted on by AI
            systems. AI Reference is a separate owned-site discoverability and
            reference diagnostic. It does not affect Content Fitness and does
            not currently measure external citations or competitor outcomes.
          </HelpCard>
          <HelpCard title="Core Web Vitals">
            Core Web Vitals are supporting technical diagnostics for loading
            performance, interaction responsiveness and visual stability. They
            add delivery context but are not content-quality measures and do not
            contribute to Content Fitness.
          </HelpCard>
          <HelpCard title="Current Paths">
            Within Content & Messaging Flows, Current Paths are inferred from
            recorded links, page roles and scan findings. They are structural
            hypotheses, not observed behaviour, and do not create a separate
            score.
          </HelpCard>
        </div>
      </div>
    </ScanShell>
  );
}

function HelpCard(props: { title: string; children: JSX.Element }) {
  return (
    <section class="signal-panel signal-help-card">
      <h2>{props.title}</h2>
      <p>{props.children}</p>
    </section>
  );
}
